// Organization settings map (docs/design/INFORMATION_ARCHITECTURE.md §3).
// Pure data: the sidebar, breadcrumbs, the palette and the Organization page
// all read it, so it holds no components. pages/Organization.jsx maps each
// `section/sub` key to the admin page that renders it.
//
// Role gates mirror the API: /api/v1/admin/* is admin + super_admin (that
// includes each organization's own SIP trunks and phone numbers: orgs bring
// their own), and /api/v1/platform/* (RTP engine, feature flags, setup
// approvals) is super_admin only, so organization admins never see the
// Platform section (it would only show 403 errors).

const SUPER_ADMIN = ["super_admin"];

// Forms and notices read best at one fixed measure; tables take the full
// pane. A section uses the form measure when every sub-page it shows is a
// form, so its width never changes between tabs (see .org-pane).
const FORM = "form";

export const ORGANIZATION_SECTIONS = [
  {
    id: "general",
    label: "General",
    icon: "building",
    purpose: "Your organization's name, profile, plan, limits and usage.",
    subs: [
      { id: "profile", label: "Profile", measure: FORM },
      { id: "usage", label: "Usage" },
    ],
  },
  {
    id: "people",
    label: "People",
    icon: "people",
    purpose: "Who works here, how they are grouped, and what each role may do.",
    subs: [
      { id: "users", label: "Users" },
      // There is no teams backend; this page assigns agents to campaigns.
      { id: "teams", label: "Assignments" },
      { id: "roles", label: "Roles" },
    ],
  },
  {
    id: "telephony",
    label: "Telephony",
    icon: "phone",
    keywords: ["sip", "trunk", "trunks", "asterisk", "phone line", "numbers", "did"],
    purpose: "How calls reach the network: your carrier trunks, phone numbers and the Asterisk connection.",
    subs: [
      { id: "trunks", label: "SIP trunks" },
      { id: "numbers", label: "Phone numbers" },
      { id: "asterisk", label: "Asterisk", measure: FORM },
    ],
  },
  {
    id: "dialing",
    label: "Dialing",
    icon: "dial",
    purpose: "The dialing mode, pacing, retries and calling hours new campaigns start with.",
    subs: [{ id: "defaults", label: "Campaign defaults", measure: FORM }],
  },
  {
    id: "audit",
    label: "Audit log",
    icon: "audit",
    purpose: "Every administrative change: who made it, what changed and when.",
    subs: [{ id: "log", label: "Audit log" }],
  },
  {
    id: "system",
    label: "System",
    icon: "pulse",
    purpose: "Service health and performance.",
    subs: [
      { id: "health", label: "Health" },
      { id: "performance", label: "Performance" },
    ],
  },
  {
    id: "platform",
    label: "Platform",
    icon: "shield",
    roles: SUPER_ADMIN,
    keywords: ["rtp", "media relay", "flags", "approvals"],
    purpose: "Workspace approvals, feature flags and the media relay across every organization.",
    subs: [
      { id: "approvals", label: "Setup approvals" },
      { id: "flags", label: "Feature flags", measure: FORM },
      { id: "rtp", label: "RTP engine" },
    ],
  },
];

/**
 * Sections that have no working control yet. They are not in the map (nothing
 * appears where its object does not exist); an old link lands on General with
 * a notice, and General names them in one line.
 */
export const NOT_YET_AVAILABLE = {
  crm: "CRM sync",
  ai: "AI configuration",
  integrations: "Outbound webhooks",
};

/**
 * Can this user open an entry gated by `roles`? Organization pages are for
 * admins; super-admin-only entries check the authenticated user's own role,
 * because the role context normalizes super_admin to admin.
 */
export const canOpenOrgEntry = (entry, user) =>
  !entry?.roles || entry.roles.includes(user?.role);

/** Sections (with their visible sub-pages) the user may open, in order. */
const ORG_ADMIN_ROLES = ["admin", "super_admin"];

export const visibleOrgSections = (user) =>
  !ORG_ADMIN_ROLES.includes(user?.role)
    ? []
    : ORGANIZATION_SECTIONS.filter((section) => canOpenOrgEntry(section, user))
    .map((section) => ({
      ...section,
      subs: section.subs.filter((sub) => canOpenOrgEntry(sub, user)),
    }))
    .filter((section) => section.subs.length > 0);

/** "form" when every visible sub-page of the section is a form, else "full". */
export const sectionMeasure = (section) =>
  section.subs.every((sub) => sub.measure === FORM) ? FORM : "full";

export const orgSectionPath = (sectionId, subId) =>
  subId ? `/organization/${sectionId}/${subId}` : `/organization/${sectionId}`;

/** The section entry for a pathname under /organization, or null. */
export const findOrgSection = (pathname) => {
  const [, root, sectionId] = pathname.split("/");
  if (root !== "organization") return null;
  return ORGANIZATION_SECTIONS.find((section) => section.id === sectionId) || null;
};

/**
 * Old /admin/* routes and where they live now. `:param` segments are copied
 * from the old URL; the query string and hash are preserved by the redirect.
 * /admin/dashboard goes to Home with the workspace redirects
 * (pages/workspace/redirects.js).
 */
export const LEGACY_ADMIN_REDIRECTS = [
  ["/admin", "/organization"],
  ["/admin/users", "/organization/people/users"],
  ["/admin/teams", "/organization/people/teams"],
  ["/admin/roles", "/organization/people/roles"],
  ["/admin/roles/:roleId/edit", "/organization/people/roles/:roleId"],
  // Access follows roles; there are no conditional access rules.
  ["/admin/access-policies", "/organization/people/roles"],
  ["/admin/organization", "/organization/general/profile"],
  ["/admin/settings", "/organization/general"],
  ["/admin/settings/*", "/organization/general"],
  ["/admin/config", "/organization/dialing/defaults"],
  // Per-campaign settings live on each campaign; calling runs through Asterisk.
  ["/admin/campaign-config", "/organization/dialing/defaults"],
  ["/admin/dialer-config", "/organization/telephony/asterisk"],
  ["/admin/crm-config", "/organization/crm"],
  ["/admin/ai-config", "/organization/ai"],
  ["/admin/audit-log", "/organization/audit"],
  ["/admin/system", "/organization/system/health"],
  // System › Logs read the audit endpoint; the audit log is its one home.
  ["/admin/system/logs", "/organization/audit"],
  ["/admin/performance", "/organization/system/performance"],
  ["/admin/sip-trunks", "/organization/telephony/trunks"],
  ["/admin/did-mappings", "/organization/telephony/numbers"],
  ["/admin/rtp-engine", "/organization/platform/rtp"],
  ["/admin/feature-flags", "/organization/platform/flags"],
  ["/admin/setup-approvals", "/organization/platform/approvals"],
  // Record analysis moves into Reports (AI tab).
  ["/admin/ai-insights", "/reports/ai"],
];
